import { ComponentProps, Dispatch, FC, ReactElement, SetStateAction, useEffect } from 'react';

import { ApiError } from '@eneco/dc/client/ApiError';
import { useAddressPublicApi } from '@eneco/hooks/address';
import { FieldValues, UseFormResetField } from 'react-hook-form';

import { AddressFinderView } from './AddressFinderView';
import { useAddressDataToContext } from './hooks/useAddressDataToContext';
import { useHouseNumberSuffixValidation } from './hooks/useHouseNumberSuffixValidation';
import { UseAddressApiHook } from './types';

interface ApiLayerProps {
  emptySuffixString: string | undefined;
  currentSelectedHouseNumberSuffix?: string;
  parameters: {
    houseNumber: string;
    houseNumberSuffix: string;
    postalCode: string;
  } | null;
  resetField: UseFormResetField<FieldValues>;
  setIsSuffixRequired: Dispatch<SetStateAction<boolean>>;
  setErrorData?: Dispatch<SetStateAction<ApiError | undefined>>;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  addressGroupLabel: string;
  useAddressApi?: UseAddressApiHook;
}

export const AddressFinderPublic: FC<
  ApiLayerProps &
    Pick<ComponentProps<typeof AddressFinderView>, 'customLabels' | 'isSuffixRequired' | 'hasAutocomplete'> & {
      customErrorNotification?: ReactElement;
    }
> = ({
  currentSelectedHouseNumberSuffix,
  customLabels,
  emptySuffixString,
  isSuffixRequired,
  parameters,
  resetField,
  setIsSuffixRequired,
  setErrorData,
  setIsLoading,
  customErrorNotification,
  hasAutocomplete,
  addressGroupLabel,
  useAddressApi = useAddressPublicApi,
}) => {
  const { data, error, houseNumberSuffixOptions, isValidating } = useAddressApi(parameters);
  const { addressData } = useAddressDataToContext(data, currentSelectedHouseNumberSuffix);

  useHouseNumberSuffixValidation({
    currentSelectedHouseNumberSuffix,
    data,
    emptySuffixString,
    error,
    houseNumberSuffixOptions,
    resetField,
    setIsSuffixRequired,
  });

  useEffect(() => {
    setIsLoading?.(isValidating);
  }, [isValidating, setIsLoading]);

  return (
    <AddressFinderView
      addressData={addressData}
      apiError={error}
      currentSelectedHouseNumberSuffix={currentSelectedHouseNumberSuffix}
      customLabels={customLabels}
      houseNumberSuffixOptions={houseNumberSuffixOptions}
      isSuffixRequired={isSuffixRequired}
      isValidating={isValidating}
      setErrorData={setErrorData}
      customErrorNotification={customErrorNotification}
      hasAutocomplete={hasAutocomplete}
      resetField={resetField}
      addressGroupLabel={addressGroupLabel}
    />
  );
};
